import { useMemo } from 'react';
import { PhoneIncoming, PhoneOutgoing, Mail, MessageSquare, Send, Calendar, Globe, XCircle, StickyNote } from 'lucide-react';
import { Badge } from '@crisellevaldez/speculo-ui';
import { Activity } from '../../../../types/activity.types';
import { HandoffNote } from '../../../../types/contact.types';
import type { GQLActivityFeedItem } from '../../../../hooks/graphql/contacts/useContact';
import {
  getEmailEventTypeDisplay,
  getEmailDescription,
  getCallDescription,
  getIconStyle,
  formatScheduledDate,
  groupActivitiesByDate,
  getFlowTypeStyle,
} from '../../../../utils/activity/activityHelpers';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useTenant } from '../../../../hooks/client/tenant/useTenant';
import { stripTrackingParams } from '../../../../utils/urlUtils';
import { buildActivityDescription } from '../../../../utils/activity/buildActivityDescription';
import { filterReturnedAnsweredCallNotes } from '../../../../utils/activity/filterReturnedAnsweredCallNotes';

// Reusable component for guest activity badges
const GuestActivityBadges = () => (
  <>
    <Badge className="badge-red items-center gap-1">
      <XCircle className="w-3 h-3" />
      logged out
    </Badge>
    <Badge className="badge-blue items-center gap-1">
      <Mail className="w-3 h-3" />
      from eAlert
    </Badge>
  </>
);

export const useProcessActivities = ({
  emailsData,
  emailCampaignsData,
  callsData,
  flowExecutionsData,
  webActivityData,
  handoffNotes,
  activityFeedData,
  eventTypeFilters,
  dateRange,
  currentPage,
  itemsPerPage,
}: {
  emailsData: any;
  emailCampaignsData: any;
  callsData: any;
  flowExecutionsData: any;
  webActivityData: any;
  handoffNotes?: HandoffNote[];
  activityFeedData?: { items: GQLActivityFeedItem[] };
  eventTypeFilters: string[];
  dateRange: { from: Date | null; to: Date | null };
  currentPage: number;
  itemsPerPage: number;
}) => {
  // Get organization and tenant data for website URLs
  const { organization } = useOrganizationStore();
  const { data: tenant } = useTenant(organization?.id);
  const websiteDomain = tenant?.domain?.primary || '';

  // Process and combine the data
  const activities = useMemo(() => {
    const result: Activity[] = [];

    // Web event types — eventType in GQL is the specific type (property_view, lead_capture, etc.), NOT "website"
    const webEventTypes = new Set(['website', 'property_view', 'property_saved', 'lead_capture', 'search', 'page_view', 'custom']);
    const excludedWebTypes = new Set(['page_view', 'custom']);

    // Process GQL activityFeed (unified feed — replaces individual Firestore sources)
    if (activityFeedData?.items?.length) {
      filterReturnedAnsweredCallNotes(activityFeedData.items).forEach((item: GQLActivityFeedItem) => {
        const isWebEvent = webEventTypes.has(item.eventType) || item.channel === 'web' || item.sourceSystem === 'web';
        // Skip excluded web event types
        if (isWebEvent && excludedWebTypes.has(item.eventType)) return;

        const timestamp = new Date(item.eventAt);
        let type: Activity['type'] = 'email';
        let title = '';
        let description: React.ReactNode = item.summary || '';
        let icon: React.ReactNode;
        let iconStyle = getIconStyle('email');

        // Parse metadata once for all event types
        let meta: Record<string, any> = {};
        try {
          meta = item.metadata ? JSON.parse(item.metadata) : {};
        } catch { /* ignore */ }

        if (item.eventType === 'call') {
          type = 'call';
          const isInbound = meta.direction === 'inbound' || item.channel === 'inbound';
          title = isInbound ? 'Incoming Call' : 'Outgoing Call';
          icon = isInbound
            ? <PhoneIncoming className="h-4 w-4 call-color-inbound" />
            : <PhoneOutgoing className="h-4 w-4 call-color-outbound font-bold" />;
          iconStyle = getIconStyle('call');
        } else if (item.eventType === 'email') {
          type = 'email';
          // outcome (= message_status) carries the lifecycle event (opened / clicked /
          // bounced / etc.). eventSubtype is direction (inbound / outbound), so
          // prefer outcome and fall back to subtype only when outcome is missing.
          title = getEmailEventTypeDisplay(item.outcome || item.eventSubtype || 'delivered');
          icon = <Mail className="h-4 w-4" />;
          iconStyle = getIconStyle('email');
        } else if (item.eventType === 'sms' || item.channel === 'sms') {
          type = 'sms';
          const isInbound = meta.direction === 'inbound' || item.channel === 'inbound' || item.eventSubtype === 'inbound';
          title = isInbound ? 'Incoming SMS' : 'Outgoing SMS';
          icon = isInbound
            ? <MessageSquare className="h-4 w-4 text-violet-700 dark:text-violet-300" />
            : <Send className="h-4 w-4 text-violet-700 dark:text-violet-300" />;
          iconStyle = { background: 'bg-violet-100 dark:bg-violet-500/15', text: '' };
        } else if (isWebEvent) {
          type = 'website';
          icon = <Globe className="h-4 w-4" />;
          iconStyle = getIconStyle('website');

          const webType = item.eventType;
          const eventCategory = meta.event_category || meta.eventCategory || '';
          const pageUrl = meta.page_url || meta.pageUrl || '';

          // Narrative title matching RecentActivities style
          if (webType === 'property_view') {
            title = (eventCategory === 'Valuation' || pageUrl?.includes('valuation'))
              ? 'Checked their home value'
              : 'Viewed a property listing';
          } else if (webType === 'lead_capture') {
            title = 'Registered on the site';
          } else if (webType === 'search') {
            title = 'Searched for homes';
          } else if (webType === 'property_saved') {
            title = 'Saved a property';
          } else {
            title = webType
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c: string) => c.toUpperCase());
          }
        } else if (item.eventType === 'note' || item.eventType === 'crm_note') {
          type = 'note';
          title = item.eventType === 'crm_note' ? 'CRM Note' : 'Note';
          icon = <StickyNote className="h-4 w-4" />;
          iconStyle = { background: 'action-icon-yellow', text: '' };
        } else {
          title = item.eventSubtype || item.eventType || 'Activity';
          icon = <Mail className="h-4 w-4" />;
        }

        // Shared description builder — same output as RecentActivities
        description = buildActivityDescription({
          eventType: item.eventType,
          eventSubtype: item.eventSubtype ?? undefined,
          channel: item.channel ?? undefined,
          summary: item.summary ?? undefined,
          outcome: item.outcome ?? undefined,
          durationSeconds: item.durationSeconds ?? undefined,
          metadata: item.metadata ?? undefined,
        });

        // Enrich sourceData with parsed metadata for ActivityItem rendering.
        // Normalize direction so getActivityIcon resolves the inbound/outbound
        // glyph for calls + SMS regardless of where the source put it.
        const enrichedSource = {
          ...item,
          event_type: isWebEvent ? item.eventType : (item.eventSubtype || item.eventType),
          direction:
            (meta.direction as string | undefined) ||
            (item.channel === 'inbound' ? 'inbound' : undefined) ||
            (item.eventSubtype === 'inbound' || item.eventSubtype === 'outbound'
              ? item.eventSubtype
              : undefined),
          event_category: meta.event_category || meta.eventCategory || '',
          page_url: meta.page_url || meta.pageUrl || '',
        };

        result.push({
          id: `feed-${item.eventId}`,
          type,
          timestamp,
          title,
          description,
          icon,
          iconStyle,
          sourceData: enrichedSource,
        });
      });
    }

    // Process emails
    if (emailsData) {
      emailsData.forEach((email: any) => {
        const style = getIconStyle('email');
        result.push({
          id: `email-${email.id}`,
          type: 'email',
          timestamp: email.timestamp,
          title: getEmailEventTypeDisplay(email.event_type),
          description: getEmailDescription(email),
          icon: <Mail className="h-4 w-4" />,
          iconStyle: style,
          sourceData: email,
        });
      });
    }

    // Process email campaigns
    if (emailCampaignsData?.pages) {
      emailCampaignsData.pages.forEach((page: any) => {
        page.campaigns.forEach((campaign: any) => {
          // Convert timestamp to Date object
          const campaignTimestamp = formatScheduledDate(campaign.scheduled_time);

          const style = {
            background: 'action-icon-yellow',
            text: '',
          };

          result.push({
            id: `email-campaign-${campaign.id}`,
            type: 'email_campaign',
            timestamp: campaignTimestamp,
            title: 'Scheduled Email',
            description: getEmailDescription({
              subject: campaign.subject_line,
              templateName: campaign.template_name,
            }),
            icon: <Calendar className="h-4 w-4" />,
            iconStyle: style,
            sourceData: campaign,
          });
        });
      });
    }

    // Process website activity
    if (webActivityData?.pages) {
      webActivityData.pages.forEach((page: any) => {
        page.activities.forEach((activity: any) => {
          // Skip excluded website subtypes
          if (excludedWebTypes.has(activity.event_type || '')) return;

          const style = getIconStyle('website');

          // Convert timestamp to Date object
          const activityTimestamp = formatScheduledDate(activity.timestamp);

          // Format event type for display
          const eventType = activity.event_type
            ? activity.event_type
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (c: string) => c.toUpperCase())
            : 'Unknown';

          // Use default globe icon for all website activities
          const icon = <Globe className="h-4 w-4" />;

          // Check if this activity has guest data (from eAlert)
          const hasGuest = activity.guest && typeof activity.guest === 'object';
          const hasGuestData =
            hasGuest &&
            (activity.guest.firstName ||
              activity.guest.lastName ||
              activity.guest.phoneNumber ||
              activity.guest.email);

          // Create description based on event type
          let description;
          const pageUrl = activity.page_url;

          // For property_view, property_saved, or lead_capture events, show property address if available
          if (
            (activity.event_type === 'property_view' ||
              activity.event_type === 'property_saved' ||
              activity.event_type === 'lead_capture') &&
            activity.properties &&
            typeof activity.properties === 'object'
          ) {
            const props = activity.properties;
            const address = props.address || '';
            const city = props.city || '';
            const state = props.state || '';
            const zipCode = props.zipCode || '';
            const cleanedUrl = stripTrackingParams(pageUrl);

            if (address && city) {
              const propertyInfo = `${address}, ${city}${state ? `, ${state}` : ''}${zipCode ? ` ${zipCode}` : ''}`;
              description = (
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={cleanedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {propertyInfo}
                  </a>
                  {hasGuestData && <GuestActivityBadges />}
                </div>
              );
            } else {
              description = (
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={cleanedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {cleanedUrl}
                  </a>
                  {hasGuestData && <GuestActivityBadges />}
                </div>
              );
            }
          }
          // For search events, show search query or filters
          else if (
            activity.event_type === 'search' &&
            activity.properties &&
            typeof activity.properties === 'object'
          ) {
            const searchQuery = activity.properties.searchQuery;
            const cleanedPageUrl = stripTrackingParams(pageUrl);

            if (searchQuery && searchQuery.trim() !== '') {
              description = (
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={cleanedPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline flex items-center gap-1.5"
                  >
                    <span>Search: {searchQuery}</span>
                  </a>
                  {hasGuestData && <GuestActivityBadges />}
                </div>
              );
            } else if (activity.properties.searchFilters?.filters) {
              // If no search query, show the search filters
              const filters = activity.properties.searchFilters.filters;
              const location = activity.properties.searchFilters.location;
              const parts = [];

              if (filters.min_price) {
                parts.push(`$${filters.min_price.toLocaleString()}+`);
              }
              if (filters.property_sub_type) {
                parts.push(filters.property_sub_type);
              }
              if (filters.standard_status) {
                parts.push(filters.standard_status);
              }
              if (location?.city) {
                parts.push(location.city);
              }
              if (location?.state) {
                parts.push(location.state);
              }

              const searchLabel = parts.join(', ');
              description = (
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={cleanedPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline flex items-center gap-1.5"
                  >
                    <span>Search: {searchLabel}</span>
                  </a>
                  {hasGuestData && <GuestActivityBadges />}
                </div>
              );
            } else {
              // Fallback to page URL if no query or filters
              description = (
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={cleanedPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {cleanedPageUrl}
                  </a>
                  {hasGuestData && <GuestActivityBadges />}
                </div>
              );
            }
          }
          // Default case: just show the URL
          else {
            const cleanedPageUrl = stripTrackingParams(pageUrl);
            description = (
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={cleanedPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {cleanedPageUrl}
                </a>
                {hasGuestData && <GuestActivityBadges />}
              </div>
            );
          }

          result.push({
            id: `website-${activity.id}`,
            type: 'website',
            timestamp: activityTimestamp,
            title: `Website ${eventType}`,
            description: description,
            icon: icon,
            iconStyle: style,
            sourceData: activity,
          });
        });
      });
    }

    // Process calls
    if (callsData?.pages) {
      callsData.pages.forEach((page: any) => {
        page.calls.forEach((call: any) => {
          const callTimestamp = call.callTimestamp?.toDate
            ? call.callTimestamp.toDate()
            : call.callTimestamp instanceof Date
              ? call.callTimestamp
              : new Date();

          const style = getIconStyle('call');
          result.push({
            id: `call-${call.id}`,
            type: 'call',
            timestamp: callTimestamp,
            title: call.inbound ? 'Incoming Call' : 'Outgoing Call',
            description: getCallDescription(call),
            icon: call.inbound ? (
              <PhoneIncoming className="h-4 w-4 call-color-inbound" />
            ) : (
              <PhoneOutgoing className="h-4 w-4 call-color-outbound font-bold" />
            ),
            iconStyle: style,
            sourceData: call,
          });
        });
      });
    }

    // Process flow executions (scheduled calls)
    if (flowExecutionsData) {
      // Filter flow executions by date range on the client side
      const filteredFlowExecutions = flowExecutionsData.filter((execution: any) => {
        const scheduledTimestamp = formatScheduledDate(execution.actualScheduledTime);

        // Check if the scheduled date is within the date range
        if (dateRange.from && dateRange.to) {
          // Set time to end of day for the to date for comparison
          const toDateWithEndOfDay = new Date(dateRange.to);
          toDateWithEndOfDay.setHours(23, 59, 59, 999);

          return scheduledTimestamp >= dateRange.from && scheduledTimestamp <= toDateWithEndOfDay;
        }

        return true; // If no date range, include all
      });

      filteredFlowExecutions.forEach((execution: any) => {
        const scheduledTimestamp = formatScheduledDate(execution.actualScheduledTime);
        const style = getIconStyle('call');

        result.push({
          id: `scheduled-call-${execution.id}`,
          type: 'scheduled_call',
          timestamp: scheduledTimestamp,
          title: 'Scheduled Call',
          description: (
            <div className="flex items-center gap-2">
              {execution.flow_name && <span>{execution.flow_name}</span>}

              {execution.flow_type && execution.flow_name && (
                <>
                  {' - '}
                  <Badge
                    className={`inline-flex items-center gap-1.5 ${getFlowTypeStyle(execution.flow_type).background} ${getFlowTypeStyle(execution.flow_type).text}`}
                  >
                    {execution.flow_type.charAt(0).toUpperCase() + execution.flow_type.slice(1)}
                  </Badge>
                </>
              )}

              {/* If flow_type exists but flow_name doesn't, don't show dash */}
              {execution.flow_type && !execution.flow_name && (
                <Badge
                  className={`inline-flex items-center gap-1.5 ${getFlowTypeStyle(execution.flow_type).background} ${getFlowTypeStyle(execution.flow_type).text}`}
                >
                  {execution.flow_type.charAt(0).toUpperCase() + execution.flow_type.slice(1)}
                </Badge>
              )}

              {execution.status && (execution.flow_name || execution.flow_type) && (
                <>
                  {' - '}
                  <Badge
                    className={`inline-flex items-center gap-1.5 ${
                      execution.status === 'paused'
                        ? 'badge-yellow'
                        : execution.status === 'active'
                          ? 'badge-green'
                          : 'bg-muted text-foreground'
                    }`}
                  >
                    {execution.status.charAt(0).toUpperCase() + execution.status.slice(1)}
                  </Badge>
                </>
              )}

              {/* If status exists but neither flow_name nor flow_type exist, don't show dash */}
              {execution.status && !execution.flow_name && !execution.flow_type && (
                <Badge
                  className={`inline-flex items-center gap-1.5 ${
                    execution.status === 'paused'
                      ? 'badge-yellow'
                      : execution.status === 'active'
                        ? 'badge-green'
                        : 'bg-muted text-foreground'
                  }`}
                >
                  {execution.status.charAt(0).toUpperCase() + execution.status.slice(1)}
                </Badge>
              )}
            </div>
          ),
          icon: <Calendar className="h-4 w-4" />,
          iconStyle: style,
          sourceData: execution,
        });
      });
    }

    // Process handoff notes
    if (handoffNotes && handoffNotes.length > 0) {
      // Filter notes by date range
      const filteredNotes = handoffNotes.filter((note) => {
        const noteTimestamp = formatScheduledDate(note.timestamp);

        if (dateRange.from && dateRange.to) {
          const toDateWithEndOfDay = new Date(dateRange.to);
          toDateWithEndOfDay.setHours(23, 59, 59, 999);

          return noteTimestamp >= dateRange.from && noteTimestamp <= toDateWithEndOfDay;
        }

        return true;
      });

      filteredNotes.forEach((note, index) => {
        const noteTimestamp = formatScheduledDate(note.timestamp);
        const style = {
          background: 'action-icon-yellow',
          text: '',
        };

        // Strip HTML tags for plain text description preview
        const plainTextNote = note.note.replace(/<[^>]*>/g, '').trim();
        const truncatedNote = plainTextNote.length > 100
          ? plainTextNote.substring(0, 100) + '...'
          : plainTextNote;

        result.push({
          id: `note-${note.timestamp}-${index}`,
          type: 'note',
          timestamp: noteTimestamp,
          title: `Note added by ${note.addedByName}`,
          description: truncatedNote,
          icon: <StickyNote className="h-4 w-4" />,
          iconStyle: style,
          sourceData: note,
        });
      });
    }

    // Sort by timestamp (newest first)
    const sortedResult = result.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    // Apply event type filters if any are selected
    if (eventTypeFilters.length > 0) {
      return sortedResult.filter((activity) => eventTypeFilters.includes(activity.type));
    }

    return sortedResult;
  }, [
    emailsData,
    emailCampaignsData,
    callsData,
    flowExecutionsData,
    webActivityData,
    handoffNotes,
    activityFeedData,
    eventTypeFilters,
    dateRange,
  ]);

  // Apply client-side pagination
  const paginatedActivities = useMemo(() => {
    const startIndex = 0; // Always start from the beginning
    return activities.slice(startIndex, (currentPage + 1) * itemsPerPage);
  }, [activities, currentPage, itemsPerPage]);

  // Group activities by date
  const groupedActivities = useMemo(() => {
    return groupActivitiesByDate(paginatedActivities);
  }, [paginatedActivities]);

  return {
    activities,
    paginatedActivities,
    groupedActivities,
    hasMore: (currentPage + 1) * itemsPerPage < activities.length,
  };
};
